import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Fila de avisos em modal para usuarios autenticados.
 * Carrega pendentes na entrada e escuta eventos em tempo real via socket.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { avisosSistemaService } from '../services/avisosSistemaService';
import { connectivityGuard } from '../services/core/connectivityGuard';
import ModalAvisoSistema from '../components/modals/ModalAvisoSistema';
const AvisosSistemaContext = createContext(undefined);
const EVENTO_AVISO = 'portal:aviso_sistema';
const INTERVALO_FALLBACK_PENDENTES_MS = 60000;
const COOLDOWN_APOS_429_MS = 5 * 60000;
/**
 * Impede o modal de aviso do sistema enquanto onboarding obrigatorio estiver pendente.
 * Deve espelhar ModalTrocarSenha (deve_trocar_senha) e ModalBoasVindas — nao usar campos inexistentes no user.
 */
function bloqueiaAvisoSistema(user, hasPermission) {
    if (!user)
        return true;
    if (user.deve_trocar_senha)
        return true;
    if (hasPermission('geral', 'visualizar_aviso_boas_vindas') &&
        !user.aviso_boas_vindas_confirmado_em) {
        return true;
    }
    return false;
}
function avisoEstaVigente(aviso, referenciaMs = Date.now()) {
    if (!aviso.exibir_ate)
        return true;
    const limiteMs = new Date(aviso.exibir_ate).getTime();
    return Number.isNaN(limiteMs) || limiteMs > referenciaMs;
}
function filtrarAvisosVigentes(fila, referenciaMs = Date.now()) {
    return fila.filter(aviso => avisoEstaVigente(aviso, referenciaMs));
}
function mesclarAviso(fila, aviso) {
    if (!aviso?.id || !avisoEstaVigente(aviso))
        return filtrarAvisosVigentes(fila);
    if (fila.some(item => item.id === aviso.id)) {
        return filtrarAvisosVigentes(fila);
    }
    return filtrarAvisosVigentes([...fila, aviso]);
}
export function AvisosSistemaProvider({ children }) {
    const { isAuthenticated, loading, refreshInProgress, token, user, hasPermission } = useAuth();
    const [fila, setFila] = useState([]);
    const [confirmando, setConfirmando] = useState(false);
    const carregouRef = useRef(false);
    const carregandoPendentesRef = useRef(null);
    const cooldownAposErroRef = useRef(0);
    const sessaoAutenticadaComToken = isAuthenticated && !loading && Boolean(token);
    const podeCarregarPendentes = sessaoAutenticadaComToken && !refreshInProgress;
    const recarregarPendentes = useCallback(async () => {
        if (carregandoPendentesRef.current) {
            return carregandoPendentesRef.current;
        }
        if (Date.now() < cooldownAposErroRef.current) {
            return;
        }
        if (!podeCarregarPendentes) {
            if (!isAuthenticated || (!loading && !token)) {
                setFila([]);
                cooldownAposErroRef.current = 0;
            }
            return;
        }
        const requisicao = (async () => {
            try {
                const pendentes = await avisosSistemaService.listarPendentes();
                cooldownAposErroRef.current = 0;
                setFila(filtrarAvisosVigentes(pendentes));
            }
            catch (erro) {
                if (axios.isAxiosError(erro) && erro.response?.status === 429) {
                    cooldownAposErroRef.current = Date.now() + COOLDOWN_APOS_429_MS;
                }
                if (!connectivityGuard.isExpectedTransient(erro)) {
                    console.warn('[AvisosSistema] Falha ao carregar pendentes:', erro);
                }
            }
            finally {
                carregandoPendentesRef.current = null;
            }
        })();
        carregandoPendentesRef.current = requisicao;
        return requisicao;
    }, [isAuthenticated, loading, podeCarregarPendentes, token]);
    // Recarrega pendentes ao autenticar ou ao trocar de usuario (novo login)
    useEffect(() => {
        if (!sessaoAutenticadaComToken) {
            setFila([]);
            carregouRef.current = false;
            carregandoPendentesRef.current = null;
            cooldownAposErroRef.current = 0;
            return;
        }
        if (refreshInProgress)
            return;
        if (carregouRef.current)
            return;
        void recarregarPendentes().finally(() => {
            carregouRef.current = true;
        });
    }, [refreshInProgress, sessaoAutenticadaComToken, user?.id, recarregarPendentes]);
    useEffect(() => {
        const handler = (event) => {
            const custom = event;
            if (custom.detail?.id) {
                setFila(prev => mesclarAviso(prev, custom.detail));
            }
        };
        window.addEventListener(EVENTO_AVISO, handler);
        return () => window.removeEventListener(EVENTO_AVISO, handler);
    }, []);
    const avisoAtual = useMemo(() => filtrarAvisosVigentes(fila)[0] ?? null, [fila]);
    const bloqueadoPorOnboarding = useMemo(() => bloqueiaAvisoSistema(user, hasPermission), [user, hasPermission]);
    const modalAberto = Boolean(avisoAtual) && sessaoAutenticadaComToken && !bloqueadoPorOnboarding;
    // Quando o onboarding termina, recarrega pendentes (fila pode ter sido carregada enquanto ainda bloqueado)
    useEffect(() => {
        if (!podeCarregarPendentes || bloqueadoPorOnboarding)
            return;
        if (carregouRef.current)
            return;
        void recarregarPendentes();
    }, [podeCarregarPendentes, bloqueadoPorOnboarding, recarregarPendentes]);
    // Fallback para quando o WebSocket nao entregar o evento: evita exigir F5 do usuario.
    useEffect(() => {
        if (!podeCarregarPendentes || bloqueadoPorOnboarding)
            return;
        const timer = window.setInterval(() => {
            void recarregarPendentes();
        }, INTERVALO_FALLBACK_PENDENTES_MS);
        return () => window.clearInterval(timer);
    }, [podeCarregarPendentes, bloqueadoPorOnboarding, recarregarPendentes]);
    // Remove avisos locais assim que o prazo maximo expira, mesmo se ja estavam na fila do cliente.
    useEffect(() => {
        if (!fila.some(aviso => aviso.exibir_ate))
            return;
        const agoraMs = Date.now();
        const vigentes = filtrarAvisosVigentes(fila, agoraMs);
        if (vigentes.length !== fila.length) {
            setFila(vigentes);
            return;
        }
        const proximosVencimentos = fila
            .map(aviso => aviso.exibir_ate ? new Date(aviso.exibir_ate).getTime() : Number.POSITIVE_INFINITY)
            .filter(limiteMs => Number.isFinite(limiteMs) && limiteMs > agoraMs);
        if (proximosVencimentos.length === 0)
            return;
        const atrasoMs = Math.min(...proximosVencimentos) - agoraMs;
        const timer = window.setTimeout(() => {
            setFila(prev => filtrarAvisosVigentes(prev));
        }, Math.min(atrasoMs, 2147483647));
        return () => window.clearTimeout(timer);
    }, [fila]);
    const confirmarAtual = useCallback(async () => {
        if (!avisoAtual?.id || confirmando)
            return;
        setConfirmando(true);
        try {
            await avisosSistemaService.confirmar(avisoAtual.id);
            setFila(prev => prev.filter(item => item.id !== avisoAtual.id));
        }
        catch (erro) {
            console.error('[AvisosSistema] Erro ao confirmar:', erro);
        }
        finally {
            setConfirmando(false);
        }
    }, [avisoAtual, confirmando]);
    const valor = useMemo(() => ({
        recarregarPendentes,
    }), [recarregarPendentes]);
    return (_jsxs(AvisosSistemaContext.Provider, { value: valor, children: [children, _jsx(ModalAvisoSistema, { aberto: modalAberto, aviso: avisoAtual, confirmando: confirmando, onConfirmar: confirmarAtual })] }));
}
export function useAvisosSistema() {
    const ctx = useContext(AvisosSistemaContext);
    if (!ctx) {
        throw new Error('useAvisosSistema deve ser usado dentro de AvisosSistemaProvider');
    }
    return ctx;
}
